/**
 * Component providing reusable Search & Sort & Page table layout.
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { SortableTable } from '@bb-ui/react-library/dist/components/SortableTable';
import { SearchBox } from '@bb-ui/react-library/dist/components/SearchBox';
import { createStyles, makeStyles, Theme } from '@bb-ui/react-library/dist/components/styles';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { TableBody } from '@bb-ui/react-library/dist/components/TableBody';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { Pagination } from 'components/Pagination';
import { PaginatedTableProps } from './PaginatedTableTypes';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(4),
    },
    searchBox: {
      maxWidth: 400,
    },
    searchRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
    matchCount: {
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
    },
    searchHelperText: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
      marginLeft: theme.spacing(1.5),
      minHeight: '1.2em',
      transition: 'opacity 0.2s ease',
    },
    textField: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    label: {
      marginBottom: 0,
      marginRight: theme.spacing(1),
      fontWeight: theme.typography.fontWeightRegular,
    },
    pagination: {
      justifyContent: 'center',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },
    /**
     * Holds the rows, or the message that stands in for them, so a caller that wants a
     * table of a settled height can bound this one box instead of the whole component.
     */
    rows: {},
    noMatchContainer: {
      display: 'flex',
      padding: theme.spacing(4),
      alignItems: 'center',
      justifyContent: 'center',
    },
  }),
);

const ITEMS_PER_PAGE = [10, 25, 50];

export const PaginatedTable = <T extends Record<string, any> | string | number>(
  props: PaginatedTableProps<T>,
) => {
  const { t } = useTranslation();
  const classes = useStyles(props);

  const {
    searchBoxProps,
    searchHelperText,
    getSortChangedAriaMessage,
    onSortChanged,
    sortableTableProps,
    paginationProps,
    perPageProps,
    sortedData,
    noMatchesMessage = (filter: string) =>
      t('global.paginatedTable.noMatches', { searchExpression: filter }),
    renderHead,
    renderRow,
    search,
    customPagination,
    hideToolbar,
    toolbarStart,
    toolbarEnd,
  } = props;

  const { value: perPageInitial, options: perPageOptions } = {
    value: ITEMS_PER_PAGE[0],
    options: ITEMS_PER_PAGE,
    ...perPageProps,
  };

  const [page, setPage] = React.useState(0);
  const [itemsPerPage, setItemsPerPage] = React.useState(perPageInitial);
  const [filter, setFilter] = React.useState('');
  const [isSearchFocused, setIsSearchFocused] = React.useState(false);

  const resetPage = React.useCallback(() => {
    setPage(0);
  }, []);

  const handlePerPageOnChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      resetPage();
      setItemsPerPage(Number(event.target.value));
    },
    [resetPage],
  );

  const handleSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        resetPage();
        setFilter(value);
      }, 250),
    [resetPage],
  );

  React.useEffect(
    () => () => {
      handleSearch.cancel();
    },
    [handleSearch],
  );

  const filteredSortedData = React.useMemo(
    () => (search && filter ? sortedData.filter(search(filter)) : sortedData),
    [filter, search, sortedData],
  );

  const matchCountLabel = t('global.paginatedTable.matchCount', {
    count: filteredSortedData.length,
    es: filteredSortedData.length !== 1 ? 'es' : '',
  });

  return (
    <>
      {!hideToolbar && (
        <div className={classes.toolbar} data-testid="paginated-table-toolbar">
          {(search || toolbarStart || toolbarEnd) && (
            <div>
              <div className={classes.searchRow}>
                {toolbarStart}
                {search && (
                  <SearchBox
                    className={classes.searchBox}
                    label={t('global.paginatedTable.searchAriaLabel')}
                    inputId="paginated-table-search-input"
                    {...searchBoxProps}
                    onChange={handleSearch}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                  />
                )}
                {toolbarEnd}
                <Typography
                  variant="body2"
                  className={classes.matchCount}
                  aria-live="polite"
                  data-testid="paginated-table-match-count"
                >
                  {matchCountLabel}
                </Typography>
              </div>
              {search && searchHelperText && (
                <Typography
                  variant="caption"
                  aria-live="polite"
                  className={classes.searchHelperText}
                  style={{ opacity: isSearchFocused && !filter ? 1 : 0 }}
                >
                  {isSearchFocused && !filter ? searchHelperText : '\u00A0'}
                </Typography>
              )}
            </div>
          )}
          <TextField
            select
            className={classes.textField}
            id="paginated-table-items-per-page"
            label={t('global.itemsPerPage')}
            value={itemsPerPage}
            onChange={handlePerPageOnChange}
            SelectProps={{ native: true }}
            InputLabelProps={{ className: classes.label }}
            data-testid="paginated-table-items-per-page"
          >
            {perPageOptions.map((perPage) => (
              <option key={perPage} value={perPage}>
                {perPage}
              </option>
            ))}
          </TextField>
        </div>
      )}

      <div className={classes.rows} data-testid="paginated-table-rows">
        {filteredSortedData.length > 0 ? (
          <SortableTable
            {...sortableTableProps}
            id="paginated-table-sortable-table"
            getAriaSortMessage={getSortChangedAriaMessage}
            onSortChanged={onSortChanged}
          >
            {renderHead()}
            <TableBody>
              {!customPagination
                ? filteredSortedData
                    .slice(page * itemsPerPage, page * itemsPerPage + itemsPerPage)
                    .map(renderRow)
                : filteredSortedData.map(renderRow)}
            </TableBody>
          </SortableTable>
        ) : (
          <div
            role="status"
            className={classes.noMatchContainer}
            data-testid="paginated-table-no-match-message"
          >
            <Typography>
              {sortedData.length > 0 ? noMatchesMessage(filter) : t('global.paginatedTable.noData')}
            </Typography>
          </div>
        )}
      </div>

      {!customPagination && (
        <Pagination
          id="paginated-table-pagination"
          className={classes.pagination}
          page={page}
          count={filteredSortedData.length}
          itemsPerPage={itemsPerPage}
          onChangePage={setPage}
          previousButtonLabel={t('global.pagination.previousButtonAriaLabel')}
          nextButtonLabel={t('global.pagination.nextButtonAriaLabel')}
          pageSelectLabel={t('global.pagination.pageSelectLabel')}
          pageSelectAriaLabel={({ page, pages }) =>
            t('global.pagination.pageSelectAriaLabel', { page, pages })
          }
          ofPages={({ pages }) => t('global.pagination.ofPages', { pages })}
          {...paginationProps}
        />
      )}
    </>
  );
};

export default PaginatedTable;
